import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { AxiosResponse } from "axios";
import { useParams } from "react-router-dom";
import { cloneDeep } from "lodash";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { ConfirmDialog } from "primereact/confirmdialog";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { TextInput } from "../../components/TextInput/TextInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { CheckboxInput } from "../../components/CheckboxInput/CheckboxInput";
import { RadioButtonInput } from "../../components/RadioButtonInput/RadioButtonInput";
import { AutocompleteInput } from "../../components/AutocompleteInput/AutocompleteInput";
import { VehicleTypeOptions } from "../../mock/StaticData";
import { ProcessingRequestListItem } from "../../models/ProcessingRequest";
import { ProcessingDispositionListItem } from "../../models/ProcessingDisposition";
import { RoleType } from "../../models/Role";
import { Partner } from "../../models/Partner";
import { Warehouse } from "../../models/Warehouse";
import { Utility } from "../../core/Utility";
import { activeUser } from "../../services/AuthService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllPartners } from "../../services/PartnerService";
import { getAllUsers } from "../../services/UserService";
import {
  getOneProcessingDisposition,
  addNewProcessingDisposition,
  updateOneProcessingDisposition,
  deleteOneProcessingDisposition,
  getOneProcessingRequest,
  getProcessingDispositionsByRequest,
} from "../../services/FinishingCenterService";
import "./ProcessingRequestsDetailsPage.scss";

export const ProcessingRequestDetailsPage = () => {
  const { t } = useTranslation();

  const [request, setRequest] = useState<ProcessingRequestListItem>();
  const [disposition, setDisposition] = useState<ProcessingDispositionListItem>(
    {
      id: "",
      requestId: "",
      dateFrom: new Date(),
      dateTo: new Date(),
      sendEmail: false,
      loadingFinished: false,
      transportationPartner: {} as Partner,
      transportationPartnerId: undefined,
      inputWarehouse: undefined,
      inputWarehouseId: undefined,
      vehicleTypeId: undefined,
      registrationNumber: "",
      driverName: "",
      price: 0,
      tollPrice: 0,
      mercantileAmount: 0,
      email: "",
      inputLocation: "",
      outputLocation: "",
    }
  );

  const [partnersApi, setPartnersApi] = useState<Partner[]>([]);
  const [warehousesApi, setWarehousesApi] = useState<Warehouse[]>([]);
  const [allDispositionsByRequest, setAllDispositionsByRequest] = useState<
    ProcessingDispositionListItem[]
  >([]);
  const [deleteConfirmVisible, setDeleteConfirmVisible] =
    useState<boolean>(false);
  const [dispositionToDelete, setDispositionToDelete] = useState<string>("");
  const [emailsOfWarehouseman, setEmailsOfWarehouseman] = useState<string[]>(
    []
  );
  const [emailsSugestions, setEmailsSugestions] = useState<string[]>([]);
  const [driverNames, setDriverNames] = useState<string[]>([]);
  const [driverNamesSugestions, setDriverNamesSugestions] = useState<string[]>(
    []
  );
  const [registrationNumbers, setRegistrationNumbers] = useState<string[]>([]);
  const [registrationNumbersSugestions, setRegistrationnumbersSugestions] =
    useState<string[]>([]);
  const [foreignRegistrationNumber, setForeignRegistrationNumber] =
    useState<boolean>(false);
  const [processing, setProcessing] = useState<boolean>(false);
  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    mercantileAmount: { value: null, matchMode: FilterMatchMode.CONTAINS },
    registrationNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
    driverName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    price: { value: null, matchMode: FilterMatchMode.CONTAINS },
    loadingFinished: { value: null, matchMode: FilterMatchMode.EQUALS },
    inputLocation: { value: null, matchMode: FilterMatchMode.CONTAINS },
    outputLocation: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const { id } = useParams();
  const toast = useRef(null);

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  useEffect(() => {
    const run = async () => {
      setProcessing(true);
      try {
        const [dispResp, partnersResp, whResp, usersResp, reqResp]: [
          AxiosResponse,
          AxiosResponse,
          AxiosResponse,
          AxiosResponse,
          AxiosResponse
        ] = await Promise.all([
          getProcessingDispositionsByRequest(id as string),
          getAllPartners(),
          getAllWarehouses(),
          getAllUsers(),
          getOneProcessingRequest(id as string),
        ]);

        [dispResp, partnersResp, whResp, usersResp, reqResp].forEach(
          ensureSuccess
        );

        const allDispByRequest = dispResp?.data?.result;
        setAllDispositionsByRequest(allDispByRequest);

        const requestData = reqResp?.data?.result;
        setRequest(requestData);

        const pList = partnersResp?.data?.result ?? [];
        setPartnersApi(pList);

        const wList = whResp?.data?.result ?? [];
        setWarehousesApi(wList);

        const uList = usersResp?.data?.result ?? [];

        let newDispositions: ProcessingDispositionListItem[] = [];
        let maxDispositionId: number = 1;

        allDispByRequest.map((dispItem: any) => {
          if (dispItem.requestId === id) {
            newDispositions.push(dispItem);
            if (
              dispItem?.dispositionIdAtRequest &&
              dispItem?.dispositionIdAtRequest >= maxDispositionId
            ) {
              maxDispositionId = dispItem.dispositionIdAtRequest + 1;
            }
          }
        });

        setDisposition((disposition) => ({
          ...disposition,
          dispositionIdAtRequest: maxDispositionId,
        }));

        //set email sugestions
        let newEmailSugestions: string[] = [];
        uList.map((item: any) => {
          newEmailSugestions.push(item?.email as string);
        });
        pList.map((onePartner: any) => {
          if (onePartner?.email && onePartner?.email !== "") {
            newEmailSugestions.push(onePartner.email);
          }
        });
        setEmailsOfWarehouseman(newEmailSugestions);

        //set unique driver name sugestions - ovo ćemo kasnije dodati kada budemo imali servise
        //let newDriverNameSugestions: string[] = [];
        // allDriverOptions.map((item: any) => newDriverNameSugestions.push(item?.driverName as string));
        // let uniqDriverNames = [ ...new Set(newDriverNameSugestions) ]
        setDriverNames([]);

        //set registration number sugestions - ovo ćemo kasnije dodati
        //let newRegistrationNumberSugestions: string[] = [];
        // allRegistrationNumbersOptions.map((item: any) => newRegistrationNumberSugestions.push((item?.registrationNumber as string).toUpperCase()));
        // let uniqRegistrationNumbers = [ ...new Set(newRegistrationNumberSugestions) ]
        setRegistrationNumbers([]);
      } catch (error: any) {
        console.log(error);
        // @ts-ignore
        toast?.current?.show({
          severity: "error",
          detail: t(`Greška pri učitavanju podataka`),
          life: 3000,
        });
      } finally {
        setProcessing(false);
      }
    };

    run();
  }, [id, t]);

  const accept = () => {
    setProcessing(true);
    deleteOneProcessingDisposition(dispositionToDelete)
      .then(() => {
        setDispositionToDelete("");
        return getProcessingDispositionsByRequest(id as string);
      })
      .then((allNewDispByRequest) => {
        setAllDispositionsByRequest(allNewDispByRequest.data.result);
      })
      .finally(() => setProcessing(false));
    //@ts-ignore
    toast.current.show({
      severity: "success",
      detail: t(`Dispozicija je obrisana`),
      life: 3000,
    });
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    mode: "all",
  });

  const filterEmails = function (e: any) {
    let results = emailsOfWarehouseman.filter((email) => {
      return email.toLowerCase().startsWith(e.query.toLowerCase());
    });
    setEmailsSugestions(results);
  };

  const filterDriverNames = function (e: any) {
    let results = driverNames.filter((driverName) => {
      return driverName.toLowerCase().startsWith(e.query.toLowerCase());
    });
    setDriverNamesSugestions(results);
  };

  const filterRegistrationNumbers = function (e: any) {
    let results = registrationNumbers.filter((registrationNumber) => {
      return registrationNumber.toLowerCase().startsWith(e.query.toLowerCase());
    });
    setRegistrationnumbersSugestions(results);
  };

  const handleNewDocumentOfReceive = (prop: string, value: any) => {
    const newDisposition = Utility.cloneDeep(disposition);
    if (
      ["mercantileAmount", "price", "tollPrice", "vehicleTypeId"].includes(prop)
    ) {
      newDisposition[prop] = value ? Math.trunc(Number(value)) : value;
    } else {
      newDisposition[prop] = value;
    }
    setDisposition(newDisposition);
  };

  const handleNewRegistrationNumber = (prop: string, value: any) => {
    const newDisposition = Utility.cloneDeep(disposition);
    // @ts-ignore
    newDisposition[prop] = value.toUpperCase();
    setDisposition(newDisposition);
  };

  const handleTransportationPartnerChange = (prop: string, value: any) => {
    let newDisposition = cloneDeep(disposition);
    newDisposition.transportationPartner = partnersApi.find(
      (item) => item.id == value
    );
    newDisposition.transportationPartnerId = value;
    setDisposition(newDisposition);
  };

  const handleWarehouseChange = (prop: string, value: any) => {
    let newDisposition = cloneDeep(disposition);
    newDisposition.inputWarehouse = warehousesApi.find((item) => item.id == value);
    newDisposition.inputWarehouseId = value;
    newDisposition.inputLocation = warehousesApi.find(
      (item) => item.id == value
    )?.name;
    setDisposition(newDisposition);
  };

  const handleForeignRegistrationNumber = () => {
    setForeignRegistrationNumber(!foreignRegistrationNumber);
  };

  const getUsedAmountWithoutCurrent = () => {
    const currentId = disposition.id; // ako je create, ovo je prazno

    return allDispositionsByRequest
      .filter(
        (d) => d.id !== currentId // isključi trenutno editovanu (ako je edit)
      )
      .reduce((sum, d) => {
        const amount = d.mercantileAmount
          ? Math.trunc(Number(d.mercantileAmount))
          : 0;
        return sum + amount;
      }, 0);
  };

  const onSubmit = () => {
    const requestAmountNum = request?.amountKg
      ? Math.trunc(Number(request.amountKg))
      : 0;

    const currentAmount = disposition.mercantileAmount
      ? Math.trunc(Number(disposition.mercantileAmount))
      : 0;

    const usedWithoutCurrent = getUsedAmountWithoutCurrent();
    const remaining = requestAmountNum - usedWithoutCurrent;

    if (currentAmount > remaining) {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: `${t("Količina dispozicije za robu")} (${currentAmount} kg) ${t(
          "je veća od ukupne količine zahteva umanjene za već kreirane dispozicije. Preostalo:"
        )} ${remaining} kg`,
        sticky: true,
      });
      return;
    }
    const mercantileAmountNum = currentAmount;

    //format multiple email
    let multipleEmail: string = "";
    if (disposition.email !== "") {
      // @ts-ignore
      disposition.email?.map((oneEmail: any) => {
        multipleEmail += oneEmail + ";";
      });
    }
    if (multipleEmail.length > 0) {
      multipleEmail = multipleEmail.slice(0, -1);
    }

    const dispositionData: any = {
      ...disposition,
      requestId: id!,
      email: multipleEmail,
      mercantileAmount: mercantileAmountNum,
      price: Number(disposition.price) || 0,
      tollPrice: Number(disposition.tollPrice) || 0,
      vehicleTypeId: Number(disposition.vehicleTypeId) || undefined,
    };

    delete dispositionData.transportationPartner;
    delete dispositionData.inputWarehouse;

    if (disposition.id) {
      updateOneProcessingDisposition(disposition.id, dispositionData).then(
        () => {
          setProcessing(true);
          getProcessingDispositionsByRequest(id as string)
            .then((allNewDispByRequest) => {
              setAllDispositionsByRequest(allNewDispByRequest.data.result);
            })
            .finally(() => setProcessing(false));
        }
      );
    } else {
      addNewProcessingDisposition(dispositionData).then(() => {
        setProcessing(true);
        getProcessingDispositionsByRequest(id as string)
          .then((allNewDispByRequest) => {
            setAllDispositionsByRequest(allNewDispByRequest.data.result);
          })
          .finally(() => setProcessing(false));
      });
    }

    setDisposition({
      id: "",
      requestId: id!,
      dateFrom: undefined,
      dateTo: undefined,
      vehicleTypeId: undefined,
      transportationPartner: undefined,
      transportationPartnerId: undefined,
      inputLocation: "",
      outputLocation: "",
      inputWarehouse: undefined,
      inputWarehouseId: undefined,
      registrationNumber: "",
      price: 0,
      tollPrice: 0,
      mercantileAmount: 0,
      driverName: "",
      email: "",
      sendEmail: false,
      loadingFinished: false,
    });

    //@ts-ignore
    toast.current.show({
      severity: "success",
      detail: t(`Uspešno dodati novi podaci`),
      life: 3000,
    });
  };

  const onError = () => {
    //@ts-ignore
    toast.current.show({
      severity: "error",
      detail: t(`Neispravno popunjena polja`),
      life: 3000,
    });
  };

  const handleResetForm = () => {
    setDisposition({
      id: "",
      requestId: id!,
      dateFrom: undefined,
      dateTo: undefined,
      vehicleTypeId: undefined,
      transportationPartner: {} as Partner,
      transportationPartnerId: undefined,
      inputLocation: "",
      outputLocation: "",
      inputWarehouse: undefined,
      inputWarehouseId: undefined,
      registrationNumber: "",
      price: 0,
      tollPrice: 0,
      mercantileAmount: 0,
      driverName: "",
      email: "",
      sendEmail: false,
      loadingFinished: false,
    });
  };

  let addButtonSaveStyle: React.CSSProperties = {};
  addButtonSaveStyle.background = "rgba(4, 102, 143, 1)";
  addButtonSaveStyle.border = "none";
  addButtonSaveStyle.margin = "0 .5rem";

  let addButtonNewPartnerStyle: React.CSSProperties = {};
  addButtonNewPartnerStyle.background = "rgba(3, 82, 115, 1)";
  addButtonNewPartnerStyle.border = "none";

  let addButtonResetStyle: React.CSSProperties = {};
  addButtonResetStyle.background = "rgba(220, 53, 69, 1)";
  addButtonResetStyle.border = "none";

  let addButtonNewCityStyle: React.CSSProperties = {};
  addButtonNewCityStyle.background = "rgba(3, 82, 115, 1)";
  addButtonNewCityStyle.border = "none";

  const onGlobalFilterChange = (e: any) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters["global"].value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const renderHeader = () => {
    return (
      <div className="header-table-container">
        <div>
          <i className="bi bi-truck header-title-icon"></i>
          <span className="header-title-content">{`${t(
            "Dispozicije za natural zahtev"
          )}`}</span>
        </div>
        <div className="search-container">
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
              placeholder="Globalna pretraga..."
            />
          </span>
        </div>
      </div>
    );
  };

  const formatDate = (rowData: ProcessingDispositionListItem) => {
    return (
      <span>{Utility.formatDate(new Date(rowData.dateFrom as Date))}</span>
    );
  };

  const formatLoadingFinished = (rowData: ProcessingDispositionListItem) => {
    return <span>{rowData.loadingFinished === true ? "Da" : "Ne"}</span>;
  };

  const formatAmount = (rowData: ProcessingDispositionListItem) => {
    const amount = rowData.mercantileAmount;
    if (amount === undefined || amount === null) return "0";
    return Math.trunc(Number(amount)).toString();
  };

  const loadingFinishedOptions = [
    { label: t("Da"), value: true },
    { label: t("Ne"), value: false },
  ];

  const loadingFinishedItemTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option.label}</span>
      </div>
    );
  };

  const loadingFinishedRowFilterTemplate = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={loadingFinishedOptions}
        onChange={(e) => {
          options.filterApplyCallback(e.value);
        }}
        itemTemplate={loadingFinishedItemTemplate}
        placeholder="Izaberite"
        className="p-column-filter"
        showClear
      />
    );
  };

  const formatVehicleType = (rowData: ProcessingDispositionListItem) => {
    return (
      <div>
        {rowData.vehicleTypeId == 1
          ? "Kamion"
          : rowData.vehicleTypeId == 2
          ? "Kamion sa prikolicom"
          : rowData.vehicleTypeId == 3
          ? "Kombi"
          : rowData.vehicleTypeId == 4
          ? "Traktor"
          : rowData.vehicleTypeId == 5
          ? "Vagon"
          : rowData.vehicleTypeId == 6
          ? "Automobil"
          : ""}
      </div>
    );
  };

  const formatOptions = (rowData: ProcessingDispositionListItem) => {
    return (
      <div className="action-buttons">
        <ButtonInput
          addStyle={{ border: "none", background: "rgba(4, 102, 143, 1)" }}
          labelText={""}
          icon="bi bi-files"
          onButtonClick={async () => {
            const id = rowData.id;
            const dispositionCopy = allDispositionsByRequest.find(
              (disposition) => disposition.id === id
            );
            if (dispositionCopy) {
              let emailArray: string[] = [];
              if (dispositionCopy.email) {
                if (typeof dispositionCopy.email === "string") {
                  emailArray = dispositionCopy.email
                    .split(";")
                    .filter((email) => email.trim() !== "");
                } else if (Array.isArray(dispositionCopy.email)) {
                  emailArray = dispositionCopy.email;
                }
              }
              const formattedCopy = {
                ...dispositionCopy,
                id: "",
                dateFrom: dispositionCopy?.dateFrom
                  ? new Date(dispositionCopy?.dateFrom)
                  : new Date(),
                dateTo: dispositionCopy?.dateTo
                  ? new Date(dispositionCopy?.dateTo)
                  : new Date(),
                vehicleTypeId: dispositionCopy.vehicleTypeId
                  ? Number(dispositionCopy.vehicleTypeId)
                  : undefined,
                mercantileAmount: 0,
                price: dispositionCopy.price
                  ? Math.trunc(Number(dispositionCopy.price))
                  : 0,
                tollPrice: dispositionCopy.tollPrice
                  ? Math.trunc(Number(dispositionCopy.tollPrice))
                  : 0,
                email: emailArray,
                loadingFinished: false,
                transportationPartnerId:
                  dispositionCopy.transportationPartner?.id ||
                  dispositionCopy.transportationPartnerId,
                inputWarehouseId:
                  dispositionCopy.inputWarehouse?.id ||
                  dispositionCopy.inputWarehouseId,
                transportationPartner:
                  dispositionCopy.transportationPartner || {},
                inputWarehouse: dispositionCopy.inputWarehouse || {},
              };
              setDisposition(
                formattedCopy as unknown as ProcessingDispositionListItem
              );
              //@ts-ignore
              toast.current.show({
                severity: "success",
                detail: t(
                  `Podaci su kopirani. Prilagodite i sačuvajte novu dispoziciju.`
                ),
                life: 3000,
              });
            }
          }}
          disabled={activeUser?.role?.name === RoleType.Director}
        />
        <ButtonInput
          addStyle={{
            border: "none",
            background: "rgba(4, 102, 143, 1)",
            margin: "0 0.6rem",
          }}
          labelText={""}
          icon="bi bi-pencil"
          onButtonClick={() => {
            const id = rowData.id ?? "";
            getOneProcessingDisposition(id).then((response: AxiosResponse) => {
              let oneDisposition = response.data.result;
              if (oneDisposition) {
                if (oneDisposition.dateFrom)
                  oneDisposition.dateFrom = new Date(oneDisposition.dateFrom);
                if (oneDisposition.dateTo)
                  oneDisposition.dateTo = new Date(oneDisposition.dateTo);
                if (
                  oneDisposition?.email &&
                  !Array.isArray(oneDisposition?.email)
                )
                  oneDisposition.email = oneDisposition?.email.split(";") ?? [];
                const formattedDisposition = {
                  ...oneDisposition,
                  mercantileAmount: oneDisposition.mercantileAmount
                    ? Math.trunc(Number(oneDisposition.mercantileAmount))
                    : 0,
                  price: oneDisposition.price
                    ? Math.trunc(Number(oneDisposition.price))
                    : 0,
                  tollPrice: oneDisposition.tollPrice
                    ? Math.trunc(Number(oneDisposition.tollPrice))
                    : 0,
                  vehicleTypeId: oneDisposition.vehicleTypeId
                    ? Number(oneDisposition.vehicleTypeId)
                    : undefined,
                  transportationPartnerId:
                    oneDisposition.transportationPartner?.id ||
                    oneDisposition.transportationPartnerId,
                  inputWarehouseId:
                    oneDisposition.inputWarehouse?.id ||
                    oneDisposition.inputWarehouseId,
                  transportationPartner:
                    oneDisposition.transportationPartner || {},
                  inputWarehouse: oneDisposition.inputWarehouse || {},
                };
                setDisposition(cloneDeep(formattedDisposition));
              }
            });
          }}
          disabled={activeUser?.role?.name === RoleType.Director}
        />
        <ButtonInput
          addStyle={{ border: "none", background: "rgba(4, 102, 143, 1)" }}
          labelText={""}
          icon="bi bi-trash"
          onButtonClick={async () => {
            const id = rowData.id ?? "";
            getOneProcessingDisposition(id).then((oneDisposition) => {
              if (oneDisposition?.data?.result?.loadingFinished === true) {
                //@ts-ignore
                toast.current.show({
                  severity: "error",
                  detail: t(
                    `Nije moguće brisati dispozicije koje su zaključene`
                  ),
                  life: 3000,
                });
              } else {
                const id = rowData.id ?? "";
                setDispositionToDelete(id);
                setDeleteConfirmVisible(true);
              }
            });
          }}
          disabled={activeUser?.role?.name === RoleType.Director}
        />
      </div>
    );
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="request-details-wrapper">
        <div className="request-details-data">
          <div
            className={"request-details-header request-details-header-natural"}
          >
            <i className="bi bi-truck"></i>{" "}
            {t("Dispozicija naturalne robe za zahtev")} {request?.number}
          </div>
          <table
            className={"request-details-table request-details-table-natural"}
          >
            <tr>
              <td className={"request-details-data-natural"}>{t("Roba")}</td>
              <td className={"request-details-data-natural"}>
                {request?.productName}
              </td>
              <td className={"request-details-data-natural"}>
                {t("Dobavljač robe")}
              </td>
              <td className={"request-details-data-natural"}>
                {request?.partnerName}
              </td>
              <td className={"request-details-data-natural"} rowSpan={3}>
                <strong>{`${t("Napomena")}:`}</strong>
                <br></br>
                {request?.note}
              </td>
            </tr>
            <tr>
              <td className={"request-details-data-natural"}>{t("Godina")}</td>
              <td className={"request-details-data-natural"}>
                {request?.harvestYear}
              </td>
              <td className={"request-details-data-natural"}>
                {t("Polazno mesto")}
              </td>
              <td className={"request-details-data-natural"}>
                {`${request?.originPlace ?? ""}
                    ${request?.address ?? ""}`}
              </td>
            </tr>
            <tr>
              <td className={"request-details-data-natural"}>
                {t("Količina zahteva (kg)")}
              </td>
              <td className={"request-details-data-natural"}>
                {request?.amountKg ? Math.trunc(request.amountKg) : ""}
              </td>
              <td className={"request-details-data-natural"}>
                {t("Trošak uračunat")}
              </td>
              <td className={"request-details-data-natural"}>
                {request?.transportCostRwa ? "Da" : "Ne"}
              </td>
            </tr>
          </table>
        </div>
        <div className="request-details-form">
          <div className="request-details-form-row">
            <DateInput
              id="dateFrom"
              value={disposition.dateFrom as Date}
              onValueChange={handleNewDocumentOfReceive}
              labelText={`${t("Datum od")} *`}
              placeholderText={t("Datum")}
              icon="bi bi-calendar-check"
              maxDate={disposition.dateTo}
              minDate={new Date()}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={disposition.loadingFinished === true}
            />
            <DateInput
              id="dateTo"
              value={disposition.dateTo as Date}
              onValueChange={handleNewDocumentOfReceive}
              labelText={`${t("Datum do")} *`}
              placeholderText={t("Datum")}
              icon="bi bi-calendar-check"
              minDate={disposition.dateFrom}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={disposition.loadingFinished === true}
            />
            <DropdownInput
              id="transportationPartner"
              value={disposition.transportationPartnerId as string}
              options={partnersApi}
              optionValue="id"
              optionLabel="name"
              onValueChange={handleTransportationPartnerChange}
              filter={true}
              labelText={`${t("Prevoznik")} *`}
              placeholderText={t("Izaberite iz liste")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={disposition.loadingFinished === true}
            />
          </div>
          <div className="request-details-form-row">
            <DropdownInput
              id="vehicleTypeId"
              value={disposition.vehicleTypeId}
              onValueChange={handleNewDocumentOfReceive}
              options={VehicleTypeOptions}
              filter={false}
              labelText={`${t("Tip vozila")} *`}
              placeholderText={t("Izaberite iz liste")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={disposition.loadingFinished === true}
            />
            <div className="registration-tables-container">
              <div className="registration-tables">
                <AutocompleteInput
                  id="registrationNumber"
                  value={disposition?.registrationNumber as string}
                  sugestions={registrationNumbersSugestions}
                  completeMethod={filterRegistrationNumbers}
                  onValueChange={handleNewRegistrationNumber}
                  labelText={`${t("Registarski broj vozila")} *`}
                  placeholder={
                    (foreignRegistrationNumber === true
                      ? t("Upišite reg. broj vozila sa stranim tablicama")
                      : t(
                          "Upišite reg. br. u formatu AA 1234AA ili AA AAA12"
                        )) as string
                  }
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={
                    foreignRegistrationNumber === false
                      ? {
                          required: true,
                          pattern: {
                            value:
                              /^[A-Z]{2}\s([0-9]{3,4}[A-Z]{2}|[A-Z]{3}[0-9]{2})$/,
                            message:
                              "Unesite registarski broj u ispravnom formatu",
                          },
                        }
                      : {
                          required: true,
                          pattern: {
                            value: /.{2,}/,
                          },
                        }
                  }
                  disabled={disposition.loadingFinished === true}
                />
              </div>
              <div className="registration-tables-foreign">
                <CheckboxInput
                  id="foreignRegistrationNumber"
                  checked={foreignRegistrationNumber}
                  onValueChange={handleForeignRegistrationNumber}
                  labelText={t("Strane tablice")}
                  control={control}
                  errors={errors}
                />
              </div>
            </div>
            <AutocompleteInput
              id="driverName"
              value={disposition?.driverName as string}
              sugestions={driverNamesSugestions}
              completeMethod={filterDriverNames}
              onValueChange={handleNewDocumentOfReceive}
              labelText={`${t("Ime vozača")} *`}
              placeholder={t("Upišite ime vozača") as string}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={disposition.loadingFinished === true}
            />
          </div>
          <div className="request-details-form-row">
            <TextInput
              id="mercantileAmount"
              value={disposition.mercantileAmount as number}
              onValueChange={handleNewDocumentOfReceive}
              labelText={`${t("Količina dispozicije (kg)")} *`}
              placeholderText={t("Unesite količinu za dispoziciju")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{
                required: true,
                pattern: {
                  value: /^\d*$/,
                  message: "Please enter a number",
                },
              }}
              disabled={disposition.loadingFinished === true}
            />
            <div className="empty-field"></div>
            <div className="empty-field"></div>
          </div>
          <div className="request-details-form-row">
            <DropdownInput
              id="inputWarehouse"
              value={disposition.inputWarehouseId}
              onValueChange={handleWarehouseChange}
              optionValue="id"
              optionLabel="name"
              options={warehousesApi.filter(
                (item) => item.finishingCenter === true
              )}
              labelText={`${t("Doradni centar")} *`}
              placeholderText={t("Izaberite iz liste")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={disposition.loadingFinished === true}
            />
            <TextInput
              id="price"
              value={disposition.price as number}
              onValueChange={handleNewDocumentOfReceive}
              labelText={`${t("Cena prevoza")} *`}
              placeholderText={t("Upišite cenu prevoza")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{
                required: true,
                pattern: {
                  value: /^\d+$/,
                  message: "Please enter a number",
                },
              }}
              disabled={disposition.loadingFinished === true}
            />
            <TextInput
              id="tollPrice"
              value={disposition.tollPrice as number}
              onValueChange={handleNewDocumentOfReceive}
              labelText={t("Putarina")}
              placeholderText={t("Upišite putarinu")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{
                pattern: {
                  value: /^\d+$/,
                  message: "Please enter a number",
                },
              }}
              disabled={disposition.loadingFinished === true}
            />
          </div>
          <div className="request-details-form-row">
            <AutocompleteInput
              id="email"
              value={disposition?.email as string}
              sugestions={emailsSugestions}
              multiple={true}
              completeMethod={filterEmails}
              onValueChange={handleNewDocumentOfReceive}
              labelText={`${t("E-mailovi magacionera")}`}
              placeholder={t("Upišite e-mailove magacionera") as string}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={disposition.loadingFinished === true}
            />
            <div className="send-email-checkbox">
              <CheckboxInput
                id="sendEmail"
                checked={disposition.sendEmail as boolean}
                onValueChange={handleNewDocumentOfReceive}
                labelText={t("Poslati mailove")}
                control={control}
                errors={errors}
              />
            </div>
            <RadioButtonInput
              id="loadingFinished"
              value={disposition.loadingFinished}
              radioButtonValue1={t("DA")}
              radioButtonValue2={t("NE")}
              onValueChange={handleNewDocumentOfReceive}
              labelText={t("Zaključena dispozicija")}
              control={control}
              errors={errors}
            />
            <div className="footer-buttons">
              <ButtonInput
                labelText={t("Sačuvaj")}
                onButtonClick={handleSubmit(onSubmit, onError)}
                addStyle={addButtonSaveStyle}
                disabled={activeUser?.role?.name === RoleType.Director}
              />
              <ButtonInput
                labelText={t("Resetuj")}
                onButtonClick={handleResetForm}
                addStyle={addButtonResetStyle}
                disabled={
                  disposition.loadingFinished === true ||
                  activeUser?.role?.name === RoleType.Director
                }
              />
            </div>
          </div>
        </div>
        <div className="request-details-dispositions">
          {processing ? (
            <ProgressSpinner />
          ) : (
            <div className="table-body">
              <DataTable
                scrollable={true}
                scrollHeight="80vh"
                sortMode="single"
                removableSort
                filters={filters}
                filterDisplay="row"
                globalFilterFields={[
                  "mercantileAmount",
                  "registrationNumber",
                  "driverName",
                  "price",
                  "inputLocation",
                  "outputLocation",
                ]}
                header={renderHeader()}
                emptyMessage="Nema kreiranih dispozicija"
                value={allDispositionsByRequest}
                showGridlines
                stripedRows
              >
                <Column
                  body={formatLoadingFinished}
                  filter
                  filterField={"loadingFinished"}
                  showFilterMenu={false}
                  filterElement={loadingFinishedRowFilterTemplate}
                  header={t("Zaključena dispozicija")}
                  style={{ minWidth: "50px" }}
                />
                <Column
                  body={formatDate}
                  header={t("Datum")}
                  style={{ minWidth: "100px" }}
                />
                <Column
                  body={formatAmount}
                  filter
                  filterField={"mercantileAmount"}
                  showFilterMenu={false}
                  style={{ minWidth: "150px" }}
                  header={t("Količina dispozicije (kg)")}
                />
                <Column
                  body={formatVehicleType}
                  header={t("Tip vozila")}
                  style={{ minWidth: "100px" }}
                />
                <Column
                  field="registrationNumber"
                  filter
                  filterField={"registrationNumber"}
                  showFilterMenu={false}
                  style={{ minWidth: "150px" }}
                  header={t("Broj registracije")}
                />
                <Column
                  field="driverName"
                  filter
                  filterField={"driverName"}
                  showFilterMenu={false}
                  style={{ minWidth: "150px" }}
                  header={t("Vozač")}
                />
                <Column
                  field="price"
                  filter
                  filterField={"price"}
                  showFilterMenu={false}
                  header={t("Cena prevoza")}
                  style={{ minWidth: "80px" }}
                />
                <Column
                  field="inputLocation"
                  filter
                  style={{ minWidth: "150px" }}
                  header={t("Mesto utovara")}
                />
                <Column
                  body={formatOptions}
                  header={t("Opcije")}
                  style={{ minWidth: "200px" }}
                />
              </DataTable>
            </div>
          )}
          <ConfirmDialog
            visible={deleteConfirmVisible}
            onHide={() => setDeleteConfirmVisible(false)}
            message={t("Da li ste sigurni da želite da obrišete dispoziciju?")}
            header={t("Potvrda brisanja dispozicije")}
            icon="bi bi-exclamation-circle"
            acceptLabel={`${t("Da")}`}
            acceptClassName="accept-button"
            accept={accept}
            rejectLabel={`${t("Ne")}`}
            rejectClassName="reject-button"
            reject={() => {}}
          />
        </div>
      </div>
    </PageLayout>
  );
};
